import { LoaderCircle, Send } from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { useAuthSession } from "../useAuthSession";
import { useVisitorContext } from "../visitorContext";
import { TurnstileWidget, type TurnstileWidgetHandle } from "./TurnstileWidget";

export type SupportRequestKind = "feedback" | "issue" | "support";

export interface SupportRequestContext {
  source: "contact_page" | "dashboard" | "header" | "room";
  roomId?: string;
  roomCode?: string;
  gameId?: string;
}

interface SupportRequestFormProps {
  initialKind?: SupportRequestKind;
  context: SupportRequestContext;
  footer?: ReactNode;
  onSubmitted?: (requestId: string) => void;
}

function browserContext(context: SupportRequestContext) {
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string };
    }
  ).connection;
  return {
    ...context,
    path: window.location.pathname,
    userAgent: navigator.userAgent.slice(0, 512),
    ...(navigator.platform ? { platform: navigator.platform.slice(0, 80) } : {}),
    ...(navigator.language ? { language: navigator.language.slice(0, 35) } : {}),
    languages: navigator.languages?.slice(0, 8),
    browserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    pixelRatio: window.devicePixelRatio,
    touchPoints: navigator.maxTouchPoints,
    ...(connection?.effectiveType ? { connectionType: connection.effectiveType.slice(0, 24) } : {}),
  };
}

export function SupportRequestForm({
  initialKind = "support",
  context,
  footer,
  onSubmitted,
}: SupportRequestFormProps) {
  const { session } = useAuthSession();
  const visitor = useVisitorContext();
  const [kind, setKind] = useState<SupportRequestKind>(initialKind);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEdited, setContactEdited] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const challengeRef = useRef<TurnstileWidgetHandle>(null);
  const handleCaptcha = useCallback((token: string | null) => setCaptchaToken(token), []);

  useEffect(() => setKind(initialKind), [initialKind]);
  useEffect(() => {
    if (!session || contactEdited) return;
    setEmail(session.user.email ?? "");
    setPhone(session.user.phone ?? "");
  }, [contactEdited, session]);

  const collectedContext = useMemo(
    () => ({
      ...browserContext(context),
      countryCode: visitor.countryCode,
      currencyCode: visitor.currencyCode,
      city: visitor.city,
      region: visitor.region,
      localeSource: visitor.source,
    }),
    [context, visitor],
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() && !phone.trim()) {
      setStatus({
        tone: "error",
        message: "Add an email or phone number so we can identify the request.",
      });
      return;
    }
    if (!captchaToken) {
      setStatus({ tone: "error", message: "Complete the security check first." });
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      const result = await api.submitFeedback({
        kind,
        subject,
        message,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        context: collectedContext,
        turnstileToken: captchaToken,
      });
      setSubject("");
      setMessage("");
      setStatus({
        tone: "success",
        message:
          result.acknowledgmentEmail === "accepted"
            ? `Thanks—ticket ${result.requestId.slice(0, 8).toUpperCase()} is open. We emailed your acknowledgment.`
            : result.acknowledgmentEmail === "failed"
              ? `Thanks—ticket ${result.requestId.slice(0, 8).toUpperCase()} is open. We could not send the acknowledgment email, but your message is safely in our support queue.`
              : `Thanks—ticket ${result.requestId.slice(0, 8).toUpperCase()} is open. Add an email next time if you would like an acknowledgment by email.`,
      });
      trackEvent("support_request_submitted", {
        request_kind: kind,
        source: context.source,
      });
      onSubmitted?.(result.requestId);
    } catch (error) {
      trackEvent("operation_failed", {
        operation: "support_submit",
        route: window.location.pathname,
      });
      setStatus({
        tone: "error",
        message: error instanceof Error ? error.message : "Your message could not be sent.",
      });
    } finally {
      setSubmitting(false);
      setCaptchaToken(null);
      challengeRef.current?.reset();
    }
  }

  return (
    <form className="support-request-form" onSubmit={(event) => void submit(event)}>
      <label>
        What can we help with?
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as SupportRequestKind)}
        >
          <option value="support">Account or game help</option>
          <option value="issue">Report a technical issue</option>
          <option value="feedback">Share feedback or an idea</option>
        </select>
      </label>
      <div className="support-contact-fields">
        <label>
          Email for our reply
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setContactEdited(true);
              setEmail(event.target.value);
            }}
          />
        </label>
        <label>
          Phone linked to your account
          <input
            type="tel"
            autoComplete="tel"
            maxLength={32}
            value={phone}
            onChange={(event) => {
              setContactEdited(true);
              setPhone(event.target.value);
            }}
          />
        </label>
      </div>
      <small className="support-context-note">
        Email or phone is required. We attach the current page, room/game identifiers, approximate
        locale, browser, and screen size so the team can reproduce issues. Never include passwords
        or sign-in codes.
      </small>
      <label>
        Subject
        <input
          required
          minLength={3}
          maxLength={160}
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
      </label>
      <label>
        Message
        <textarea
          required
          minLength={10}
          maxLength={4000}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </label>
      <TurnstileWidget ref={challengeRef} action="feedback_submit" onToken={handleCaptcha} />
      {status && (
        <p className={`contact-form-status ${status.tone}`} role="status">
          {status.message}
        </p>
      )}
      <div className="support-form-actions">
        {footer}
        <button type="submit" disabled={submitting || !captchaToken}>
          {submitting ? <LoaderCircle className="contact-spinner" /> : <Send />}
          {submitting
            ? "Sending…"
            : kind === "feedback"
              ? "Send feedback"
              : kind === "issue"
                ? "Report issue"
                : "Send message"}
        </button>
      </div>
    </form>
  );
}
